import i18next from 'i18next';
import { isArray, isNumber } from 'lodash';
import { DateTime } from 'luxon';
import React, { memo, ReactElement, useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { Row } from 'react-bootstrap';
import { ApexOptions } from 'apexcharts';
import { PromGraphDto, PromGraphNamedSeriesDto, PromQuestionLocalizedContentDto } from "../../../../../shared/dtos/prom.dto";
import { PromQuestionType } from "../../../../../shared/utils/enums";
const xType = 'datetime' as const;

const enduranceSeriesColors: string[] = ['#4e79a7', '#f28e2b', '#59a14f', '#e15759', '#b07aa1', '#76b7b2', '#edc948'];

type AnswerData = {
  options: ApexOptions;
  series: ApexAxisChartSeries;
  question: {
    title: string,
    content: string,
  },
  lowKey: string;
  highKey: string;
}

const initApexChart: ApexChart = {
  id: 'Questionnaire-Graph',
  height: 400,
  width: 500,
  zoom: {
    enabled: false,
  },
  toolbar: {
    show: false,
  },
  defaultLocale: 'de',
  locales: []
};

const answerFormatter = function (val: string | number | number[]): string {
  if (isNumber(val)) {
    return val > 0 ? val.toString() : '0';
  }
  if (isArray(val)) {
    return val.map(num => num.toFixed(2)).join();
  }
  return '';
};

const initData: AnswerData = {
  options: {
    chart: initApexChart,
    colors: ['#ff695f'],
    dataLabels: {
      enabled: true,
      formatter: answerFormatter,
      style: {
        fontSize: '16px',
        fontWeight: 'bold',
      },
    },
    xaxis: {
      type: xType,
      min:undefined,
      max:undefined
    },
    labels: [],
    yaxis: {
      min: 0,
      max: 10,
      decimalsInFloat: 0,
    },
    legend: {
      show: false,
    },
  },
  series: [],
  question: {
    title: '',
    content: '',
  },
  lowKey: '',
  highKey: ''

};

type Props = {
  promGraph: PromGraphDto
}

const Answer = ({ promGraph }: Props): ReactElement => {
  initData.options.chart ??= initApexChart;
  initData.options.chart.locales=[{
    name: 'de',
    options: {
      months: i18next.t('answerAndActivity.months').split(','),
      shortMonths: i18next.t('answerAndActivity.shortMonths').split(','),
    }
  },
  {
    name: 'en',
    options: {
      months: i18next.t('answerAndActivity.months').split(','),
      shortMonths: i18next.t('answerAndActivity.shortMonths').split(','),
    }
  }];
  const [answerData, setAnswer] = useState<AnswerData>(initData);
  const userLanguage = i18next.language;
  initData.options.labels=[];
  promGraph.series.forEach((entry) => {
    if (!!initData.options.labels && !!userLanguage) {
      initData.options.labels.push(DateTime.fromMillis(entry[0], { zone: 'utc' }).setLocale(userLanguage).toFormat('dd MMM'));
    }
  });
  useEffect(() => {
    if (promGraph.promQuestion) {
      const question: PromQuestionLocalizedContentDto | undefined = promGraph.promQuestion.localizeContent.find(question => question.languageCode === userLanguage);
      if (!question) {
        return;
      }
      
      const tempObj = JSON.parse(JSON.stringify(initData));

      const isRangeQuestion: boolean = promGraph.promQuestion.questionType === PromQuestionType.RANGE;

      tempObj.options.yaxis = isRangeQuestion
        ? { min: 0, max: 10, decimalsInFloat: 0 }
        : { min: 0, decimalsInFloat: 2 };

      if (promGraph.seriesByType && promGraph.seriesByType.length > 0) {
        tempObj.series = promGraph.seriesByType.map((namedSeries: PromGraphNamedSeriesDto) => ({
          name: namedSeries.name,
          type: 'line',
          data: namedSeries.series,
        }));
        tempObj.options.colors = enduranceSeriesColors;
        tempObj.options.legend = { show: true, position: 'top' };
        tempObj.options.dataLabels = { enabled: true, formatter: answerFormatter };
        tempObj.options.markers = { size: 5 };
        tempObj.options.stroke = { curve: 'straight', width: 2 };
      } else {
        tempObj.series = [{
          name: question.title,
          type: 'line',
          data: promGraph.series,
        }];
      }

      tempObj.question = { title: question.title, content: question.questionText };
      tempObj.highKey = question.rangeLabels?.max ?? '';
      tempObj.lowKey = question.rangeLabels?.min ?? '';
      setAnswer(tempObj);
    }
  }, [promGraph, userLanguage]);

  return (
    <div className="app">
      <Row>
        <div className="ps-4 pb-1">
          <h4>
            <b>{answerData.question.title}</b>
          </h4>
          <p className="mb-1" style={{ color: '#707089', lineHeight: '21px' }}>{answerData.question.content}</p>
          {answerData.lowKey && answerData.highKey && (
            <span style={{ color: '#FF695F' }}>{`0 - ${answerData.lowKey}`}, {`10 - ${answerData.highKey}`}</span>
          )}
        </div>
        <div className="mixed-chart" style={{ width: 500 }}>
          <Chart options={answerData.options} series={answerData.series} type="line" />
        </div>
      </Row>
    </div>
  );
};

export default memo(Answer);
